// WHICH LANGUAGE THE APP RENDERS IN — the selection half of i18n.
//
// Which locales exist is the registry's decision
// (packages/contracts/src/locales.ts, D-430); which of them a user may pick is
// i18n-completeness.ts's, because a locale whose dictionaries are still English
// must not be offered as if it were done.
//
// RESOLUTION ORDER, highest first (`resolveLocale`):
//
//   1. `?lng=` on the URL — an explicit, shareable override, and the handle a
//      recorded walkthrough drives a screen with. It is kept for the rest of
//      the tab's life, so client-side navigation does not drop it.
//   2. The user's SAVED language — `GET /v1/auth/profile` with
//      `locale_saved: true`, i.e. a language the user picked with the switcher
//      on any device. This is what makes a choice follow a person to a second
//      phone, and what gives each worker on a shared floor tablet their own
//      language. A stored default (an invite's language, or `en`) is not a
//      choice and does not count.
//   3. This device's stored choice (`localStorage["i18nextLng"]`, i18next's own
//      key, which the e2e harness also seeds).
//   4. The browser's language list, when it names a selectable locale. Tags
//      are read through the registry's `normalizeLocale`, so `zh-TW` reads as
//      Traditional Chinese and never as Simplified.
//   5. The tenant's default language, for a browser in a language we do not
//      ship (a `kk-KZ` phone at a Russian-default plant gets Russian).
//   6. `en`.
//
// Steps 4 and 5 are deliberately in that order, not the reverse: a tenant
// default above the browser would turn every English browser on a
// Russian-default tenant into a Russian one, the e2e harness included, and the
// browser is the more personal of the two signals (decided in P3-UX L3, see
// spec 16 §3.10).
//
// Boot resolves synchronously from what the page has (1, 3, 4, 6). The profile
// arrives after sign-in, and `localeAfterProfile` then re-resolves with 2 and
// 5 and the switcher changes language if the answer moved.

import {
  LOCALE_META,
  normalizeLocale,
  SUPPORTED_LOCALES,
  type Locale,
} from "@assessly/contracts/locales";
import { LOCALE_COMPLETENESS } from "../i18n-completeness.js";

/** i18next's conventional storage key — kept so a detector plugin can adopt it. */
export const LOCALE_STORAGE_KEY = "i18nextLng";

/** The `?lng=` query parameter, shared with the e2e `preferLocale()` helper. */
export const LOCALE_QUERY_PARAM = "lng";

/** Marks, for this tab only, that `?lng=` chose the language (step 1). */
export const LOCALE_EXPLICIT_SESSION_KEY = "assessly.lng.explicit";

/**
 * A switcher choice the profile has not accepted yet: the PATCH failed (no
 * signal on the floor, a password that must change first) or has not
 * answered. While it stands, the user's saved language (step 2) is skipped
 * for that user, so the stale saved value cannot take the choice back, and the
 * switcher sends it again on its next mount and when the device comes back
 * online. `user_id` is null when the choice was made before the profile was
 * known; sign-in and sign-out drop such a choice, because it cannot be
 * attributed to the next person on a shared tablet.
 */
export const LOCALE_UNSAVED_KEY = "assessly.lng.unsaved";

/**
 * Locales a user may choose: the source locale plus every locale that CLAIMS a
 * real translation. Derived from the completeness claim rather than restated,
 * so finishing a translation is one edit (i18n-completeness.ts) and the
 * switcher follows.
 */
export const SELECTABLE_LOCALES: readonly Locale[] = SUPPORTED_LOCALES.filter(
  (l) => LOCALE_COMPLETENESS[l].status !== "placeholder",
);

/**
 * Endonyms — each locale's name IN ITSELF, never translated, from the registry.
 * A Russian speaker looking for their language scans for "Русский", not for
 * whatever the current UI language calls Russian.
 */
export const LOCALE_ENDONYMS: Readonly<Record<Locale, string>> = Object.fromEntries(
  SUPPORTED_LOCALES.map((l) => [l, LOCALE_META[l].endonym]),
) as Record<Locale, string>;

/** The BCP 47 tag `<html lang>` carries: `zh` is written `zh-Hans`. */
export function htmlLangOf(locale: Locale): string {
  return LOCALE_META[locale].htmlLang;
}

export function isSelectableLocale(value: unknown): value is Locale {
  return typeof value === "string" && SELECTABLE_LOCALES.includes(value as Locale);
}

/** A tag from any source, narrowed to a selectable locale or undefined. */
export function selectableFromTag(tag: string | null | undefined): Locale | undefined {
  const locale = normalizeLocale(tag);
  return locale !== undefined && isSelectableLocale(locale) ? locale : undefined;
}

/** Everything the resolution order reads. Each is a raw tag or absent. */
export interface LocaleSignals {
  readonly query?: string | null | undefined;
  /** The profile's `locale`, counted only when `savedByUser` is true. */
  readonly userLocale?: string | null | undefined;
  readonly savedByUser?: boolean | undefined;
  readonly device?: string | null | undefined;
  readonly browser?: readonly string[] | undefined;
  readonly tenantDefault?: string | null | undefined;
}

/** The resolution order in the header, as a pure function. */
export function resolveLocale(signals: LocaleSignals): Locale {
  const fromQuery = selectableFromTag(signals.query);
  if (fromQuery) return fromQuery;
  if (signals.savedByUser === true) {
    const fromUser = selectableFromTag(signals.userLocale);
    if (fromUser) return fromUser;
  }
  const fromDevice = selectableFromTag(signals.device);
  if (fromDevice) return fromDevice;
  for (const tag of signals.browser ?? []) {
    const fromBrowser = selectableFromTag(tag);
    if (fromBrowser) return fromBrowser;
  }
  return selectableFromTag(signals.tenantDefault) ?? "en";
}

type StorageKind = "localStorage" | "sessionStorage";

/** Storage can throw (Safari private mode, embedded webviews) — never fatal. */
function readKey(kind: StorageKind, key: string): string | undefined {
  try {
    return (globalThis as Partial<Record<StorageKind, Storage>>)[kind]?.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeKey(kind: StorageKind, key: string, value: string): void {
  try {
    (globalThis as Partial<Record<StorageKind, Storage>>)[kind]?.setItem(key, value);
  } catch {
    /* storage blocked; the choice still applies for this page's lifetime */
  }
}

export function storeLocale(locale: Locale): void {
  writeKey("localStorage", LOCALE_STORAGE_KEY, locale);
}

function readQuery(): string | undefined {
  try {
    const loc = (globalThis as { location?: { search?: string } }).location;
    if (!loc?.search) return undefined;
    return new URLSearchParams(loc.search).get(LOCALE_QUERY_PARAM) ?? undefined;
  } catch {
    return undefined;
  }
}

function readBrowser(): readonly string[] {
  const nav = (globalThis as { navigator?: { languages?: readonly string[]; language?: string } })
    .navigator;
  if (!nav) return [];
  return nav.languages?.length ? nav.languages : nav.language ? [nav.language] : [];
}

/** Did `?lng=` choose the language in this tab? */
export function explicitInThisTab(): boolean {
  return readKey("sessionStorage", LOCALE_EXPLICIT_SESSION_KEY) === "1";
}

/**
 * The locale to render with on this page load, before any profile is known.
 * A `?lng=` override is persisted to the device and marked explicit for the
 * tab: a walkthrough that lands on `/floor?lng=ru` then navigates client-side
 * (dropping the query) must stay in Russian, and the user's saved language
 * must not take it back.
 */
export function resolveInitialLocale(): Locale {
  const query = selectableFromTag(readQuery());
  if (query) {
    storeLocale(query);
    writeKey("sessionStorage", LOCALE_EXPLICIT_SESSION_KEY, "1");
    return query;
  }
  return resolveLocale({
    device: readKey("localStorage", LOCALE_STORAGE_KEY),
    browser: readBrowser(),
  });
}

/** The profile fields the resolution order reads (`GET /v1/auth/profile`). */
export interface ProfileLocaleFacts {
  readonly user_id: string;
  readonly locale: string;
  readonly locale_saved: boolean;
  readonly tenant_default_locale: string | null;
  /** A platform staff member is using the session through a grant. */
  readonly impersonated: boolean;
  /** The S19 banner's facts while `impersonated` (spec 15 §4 S19, D-492);
   * absent from a profile served before M15-8. */
  readonly impersonation?: {
    readonly grant_id: string;
    readonly staff_display_name: string | null;
    readonly ticket_ref: string | null;
    readonly mode: "read_only" | "act_as";
    readonly expires_at: string;
  } | null;
}

/** See LOCALE_UNSAVED_KEY. */
export interface UnsavedLocaleChoice {
  readonly user_id: string | null;
  readonly locale: Locale;
}

export function readUnsavedChoice(): UnsavedLocaleChoice | undefined {
  const raw = readKey("localStorage", LOCALE_UNSAVED_KEY);
  if (raw === undefined) return undefined;
  try {
    const parsed = JSON.parse(raw) as { user_id?: unknown; locale?: unknown };
    const userId = parsed.user_id;
    if (!isSelectableLocale(parsed.locale)) return undefined;
    if (userId !== null && typeof userId !== "string") return undefined;
    return { user_id: userId, locale: parsed.locale };
  } catch {
    return undefined;
  }
}

export function writeUnsavedChoice(choice: UnsavedLocaleChoice): void {
  writeKey("localStorage", LOCALE_UNSAVED_KEY, JSON.stringify(choice));
}

export function clearUnsavedChoice(): void {
  try {
    (globalThis as Partial<Record<StorageKind, Storage>>).localStorage?.removeItem(
      LOCALE_UNSAVED_KEY,
    );
  } catch {
    /* storage blocked; nothing was stored either */
  }
}

/** Is there a choice this user made on this device that the profile has not taken yet? */
export function unsavedChoiceFor(userId: string): UnsavedLocaleChoice | undefined {
  const choice = readUnsavedChoice();
  if (choice === undefined) return undefined;
  return choice.user_id === null || choice.user_id === userId ? choice : undefined;
}

/**
 * The locale to switch to once the profile is known, or undefined when the
 * current one stands (an explicit `?lng=` this tab, or no change).
 *
 * The saved language does not count in an impersonation session (the
 * language on screen is the staff member's) nor while this user has a choice
 * on this device that the profile has not accepted yet.
 */
export function localeAfterProfile(
  profile: ProfileLocaleFacts,
  current: string,
): Locale | undefined {
  if (explicitInThisTab()) return undefined;
  const savedCounts =
    profile.locale_saved &&
    !profile.impersonated &&
    unsavedChoiceFor(profile.user_id) === undefined;
  const next = resolveLocale({
    userLocale: profile.locale,
    savedByUser: savedCounts,
    device: readKey("localStorage", LOCALE_STORAGE_KEY),
    browser: readBrowser(),
    tenantDefault: profile.tenant_default_locale,
  });
  return next === current ? undefined : next;
}
